import { useEffect, useRef, useState } from "react";
import { CheckCircle2, Download, FileSpreadsheet, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ToggleGroup,
  ToggleGroupItem,
} from "@/components/ui/toggle-group";
import {
  analyticsByCategoryDeep,
  analyticsByVendor,
  categoryForecast,
  listCategories,
  listExpenses,
  listOccurrences,
  listPayments,
  listVendors,
  monthlyForecast,
  varianceByCategory,
  varianceMonthly,
} from "@/lib/api";
import { exportSheets, buildFilename } from "@/lib/export";
import { recurrenceIT, statusIT } from "@/lib/format";

/**
 * "Esporta tutto il periodo" — single multi-sheet workbook for monthly archive.
 *
 * Sheets included:
 *   1. Riepilogo            (cover with parameters + headline KPIs)
 *   2. Scadenze             (filtered occurrences in the period)
 *   3. Pagamenti            (payments with occurrence/expense/vendor enrichment)
 *   4. Scostamento mensile  (monthly variance, basis selected)
 *   5. Scostamento categorie
 *   6. Analisi fornitori
 *   7. Analisi categorie
 *   8. Previsione mensile   (forecast year derived from period)
 *   9. Previsione categorie
 */

const today = () => new Date().toISOString().slice(0, 10);
const startOfYear = () => `${new Date().getFullYear()}-01-01`;
const endOfYear = () => `${new Date().getFullYear()}-12-31`;

const inRange = (iso, from, to) => iso >= from && iso <= to;

export function MonthlyReportButton({
  defaultDateFrom,
  defaultDateTo,
  defaultBasis = "accrual",
  defaultVendorId = "",
  defaultCategoryId = "",
  vendors: presetVendors,
  categories: presetCategories,
  testId = "monthly-report",
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  // 'idle' (form visible) | 'done' (success message inside the dialog)
  const [phase, setPhase] = useState("idle");
  const [downloadedName, setDownloadedName] = useState("");
  const [dateFrom, setDateFrom] = useState(defaultDateFrom || startOfYear());
  const [dateTo, setDateTo] = useState(defaultDateTo || endOfYear());
  const [basis, setBasis] = useState(defaultBasis);
  const [vendorId, setVendorId] = useState(defaultVendorId);
  const [categoryId, setCategoryId] = useState(defaultCategoryId);
  const [vendors, setVendors] = useState(presetVendors || []);
  const [categories, setCategories] = useState(presetCategories || []);
  const [errorMessage, setErrorMessage] = useState("");

  // Track unmount to avoid setting state after dialog/component is gone.
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  // Fetch lookups on first dialog open. Decoupled from the click handler so
  // we never await async work inside Radix's onOpenChange / button click,
  // which is a common cause of portal/insertBefore races.
  useEffect(() => {
    if (!open) return;
    if (vendors.length > 0 && categories.length > 0) return;
    let cancelled = false;
    (async () => {
      try {
        const [v, c] = await Promise.all([
          vendors.length === 0 ? listVendors() : Promise.resolve(vendors),
          categories.length === 0 ? listCategories() : Promise.resolve(categories),
        ]);
        if (cancelled || !aliveRef.current) return;
        setVendors(v || []);
        setCategories(c || []);
      } catch (e) {
        // non-fatal — user can still pick "Tutti / Tutte"
        // eslint-disable-next-line no-console
        console.error("Failed to fetch report lookups", e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, vendors, categories]);

  const handleOpenChange = (next) => {
    if (busy && !next) return; // don't close while generating
    setOpen(next);
    if (next) {
      // Reset to the form view every time the dialog opens.
      setPhase("idle");
      setDownloadedName("");
      setErrorMessage("");
    }
  };

  const generate = async () => {
    if (busy) return;
    setErrorMessage("");
    if (dateFrom > dateTo) {
      setErrorMessage("La data 'Dal' deve precedere la data 'Al'.");
      return;
    }
    setBusy(true);
    try {
      const baseParams = { date_from: dateFrom, date_to: dateTo };
      const variantParams = { ...baseParams, basis };

      // The forecast endpoints are year-based; use the year of the start date.
      const year = Number(dateFrom.slice(0, 4));

      // Occurrence filter accepts vendor/category at API level
      const occurrenceParams = {
        ...baseParams,
        limit: 1000,
        ...(vendorId ? { vendor_id: vendorId } : {}),
        ...(categoryId ? { category_id: categoryId } : {}),
      };

      const [
        venList,
        catList,
        expenses,
        occurrences,
        payments,
        varMonthly,
        varCategory,
        anByVendor,
        anByCategory,
        fcMonthly,
        fcCategory,
      ] = await Promise.all([
        vendors.length ? Promise.resolve(vendors) : listVendors(),
        categories.length ? Promise.resolve(categories) : listCategories(),
        listExpenses(),
        listOccurrences(occurrenceParams),
        listPayments({ ...baseParams, limit: 5000 }),
        varianceMonthly(variantParams),
        varianceByCategory(variantParams),
        analyticsByVendor(variantParams),
        analyticsByCategoryDeep(variantParams),
        monthlyForecast({ year }),
        categoryForecast({ year }),
      ]);

      const venMap = Object.fromEntries((venList || []).map((v) => [v.id, v]));
      const catMap = Object.fromEntries((catList || []).map((c) => [c.id, c]));
      const expMap = Object.fromEntries((expenses || []).map((e) => [e.id, e]));
      const occMap = Object.fromEntries((occurrences || []).map((o) => [o.id, o]));

      // Apply additional vendor/category filter to payments via their occurrence
      const filteredPayments = (payments || []).filter((p) => {
        const occ = occMap[p.occurrence_id];
        if (!occ) return false; // payment outside occurrence window
        if (vendorId) {
          const vid = occ.vendor_id_override || expMap[occ.expense_id]?.vendor_id;
          if (vid !== vendorId) return false;
        }
        if (categoryId) {
          const cid =
            occ.category_id_override || expMap[occ.expense_id]?.category_id;
          if (cid !== categoryId) return false;
        }
        return true;
      });

      // -------- Build sheets --------
      const vendorLabel = vendorId ? venMap[vendorId]?.name || vendorId : "Tutti";
      const categoryLabel = categoryId
        ? catMap[categoryId]?.name || categoryId
        : "Tutte";

      const totalPlanned = (occurrences || []).reduce(
        (s, o) => s + Number(o.effective_amount || 0),
        0,
      );
      const totalPaid = (occurrences || []).reduce(
        (s, o) => s + Number(o.total_paid || 0),
        0,
      );
      const overdueCount = (occurrences || []).filter(
        (o) =>
          (o.status === "planned" || o.status === "partially_paid") &&
          o.due_date < today(),
      ).length;

      const sheets = [];

      sheets.push({
        name: "Riepilogo",
        rows: [
          ["Periodo (dal)", dateFrom],
          ["Periodo (al)", dateTo],
          ["Base di analisi", basis === "cash" ? "Cassa" : "Competenza"],
          ["Fornitore", vendorLabel],
          ["Categoria", categoryLabel],
          [
            "Generato il",
            new Date().toISOString().slice(0, 19).replace("T", " "),
          ],
          ["", ""],
          ["Numero scadenze nel periodo", String((occurrences || []).length)],
          ["Numero pagamenti nel periodo", String(filteredPayments.length)],
          ["Scadute non saldate", String(overdueCount)],
          ["Totale pianificato (€)", totalPlanned],
          ["Totale pagato (€)", totalPaid],
          ["Δ residuo (€)", totalPlanned - totalPaid],
        ].map(([k, v]) => ({
          metric: k,
          text: typeof v === "number" ? null : v,
          amount: typeof v === "number" ? v : null,
        })),
        columns: [
          { key: "metric", header: "Voce" },
          { key: "text", header: "Dettaglio" },
          { key: "amount", header: "Importo (€)", type: "currency" },
        ],
      });

      // Scadenze
      sheets.push({
        name: "Scadenze",
        rows: (occurrences || []).map((o) => {
          const exp = expMap[o.expense_id];
          const cid = o.category_id_override || exp?.category_id;
          const vid = o.vendor_id_override || exp?.vendor_id;
          return {
            due_date: o.due_date,
            original_due_date: o.original_due_date,
            postponed: o.original_due_date !== o.due_date ? "Sì" : "No",
            sequence_no: o.sequence_no,
            title: exp?.title || "",
            recurrence_type: recurrenceIT[exp?.recurrence_type] || "",
            status: statusIT[o.status] || o.status,
            modified: o.is_modified ? "Sì" : "No",
            vendor_name: venMap[vid]?.name || "",
            category_name: catMap[cid]?.name || "",
            planned: o.effective_amount,
            paid: o.total_paid,
            variance:
              Number(o.effective_amount || 0) - Number(o.total_paid || 0),
            notes: o.notes || "",
          };
        }),
        columns: [
          { key: "due_date", header: "Scadenza", type: "date" },
          { key: "original_due_date", header: "Scadenza originale", type: "date" },
          { key: "postponed", header: "Rinviata" },
          { key: "sequence_no", header: "Seq.", type: "integer" },
          { key: "title", header: "Descrizione" },
          { key: "recurrence_type", header: "Ricorrenza" },
          { key: "status", header: "Stato" },
          { key: "modified", header: "Modificata" },
          { key: "vendor_name", header: "Fornitore" },
          { key: "category_name", header: "Categoria" },
          { key: "planned", header: "Pianificato (€)", type: "currency" },
          { key: "paid", header: "Pagato (€)", type: "currency" },
          { key: "variance", header: "Δ residuo (€)", type: "currency" },
          { key: "notes", header: "Note" },
        ],
      });

      // Pagamenti (enriched)
      sheets.push({
        name: "Pagamenti",
        rows: filteredPayments.map((p) => {
          const occ = occMap[p.occurrence_id];
          const exp = occ ? expMap[occ.expense_id] : null;
          const cid = occ
            ? occ.category_id_override || exp?.category_id
            : null;
          const vid = occ ? occ.vendor_id_override || exp?.vendor_id : null;
          return {
            paid_at: p.paid_at,
            due_date: occ?.due_date || "",
            title: exp?.title || "",
            vendor_name: vid ? venMap[vid]?.name || "" : "",
            category_name: cid ? catMap[cid]?.name || "" : "",
            method: p.method || "",
            reference: p.reference || "",
            paid_amount: p.paid_amount,
          };
        }),
        columns: [
          { key: "paid_at", header: "Data pagamento", type: "date" },
          { key: "due_date", header: "Scadenza riferita", type: "date" },
          { key: "title", header: "Descrizione" },
          { key: "vendor_name", header: "Fornitore" },
          { key: "category_name", header: "Categoria" },
          { key: "method", header: "Metodo" },
          { key: "reference", header: "Riferimento" },
          { key: "paid_amount", header: "Importo (€)", type: "currency" },
        ],
      });

      // Scostamento mensile
      sheets.push({
        name: "Scostamento mensile",
        rows: (varMonthly || []).map((r) => ({
          label: r.label,
          planned: r.planned,
          actual: r.actual,
          variance: r.variance,
          variance_pct: r.variance_pct,
        })),
        columns: [
          { key: "label", header: "Mese" },
          { key: "planned", header: "Pianificato (€)", type: "currency" },
          {
            key: "actual",
            header: basis === "cash" ? "Pagato cassa (€)" : "Pagato competenza (€)",
            type: "currency",
          },
          { key: "variance", header: "Δ (€)", type: "currency" },
          { key: "variance_pct", header: "Δ %", type: "percent" },
        ],
      });

      // Scostamento per categoria
      sheets.push({
        name: "Scostamento categorie",
        rows: (varCategory || []).map((r) => ({
          label: r.label,
          planned: r.planned,
          actual: r.actual,
          variance: r.variance,
        })),
        columns: [
          { key: "label", header: "Categoria" },
          { key: "planned", header: "Pianificato (€)", type: "currency" },
          { key: "actual", header: "Pagato (€)", type: "currency" },
          { key: "variance", header: "Δ (€)", type: "currency" },
        ],
      });

      // Analisi fornitori
      sheets.push({
        name: "Analisi fornitori",
        rows: (anByVendor || []).map((r) => ({
          vendor_name: r.vendor_name,
          occurrences: r.occurrences,
          payments: r.payments,
          planned: r.planned,
          actual: r.actual,
          variance: r.variance,
          variance_pct: r.variance_pct,
        })),
        columns: [
          { key: "vendor_name", header: "Fornitore" },
          { key: "occurrences", header: "Scadenze", type: "integer" },
          { key: "payments", header: "Pagamenti", type: "integer" },
          { key: "planned", header: "Pianificato (€)", type: "currency" },
          { key: "actual", header: "Pagato (€)", type: "currency" },
          { key: "variance", header: "Δ (€)", type: "currency" },
          { key: "variance_pct", header: "Δ %", type: "percent" },
        ],
      });

      // Analisi categorie
      const totalActual = (anByCategory || []).reduce(
        (s, r) => s + Number(r.actual || 0),
        0,
      );
      sheets.push({
        name: "Analisi categorie",
        rows: (anByCategory || []).map((r) => ({
          category_name: r.category_name,
          occurrences: r.occurrences,
          planned: r.planned,
          actual: r.actual,
          variance: r.variance,
          share_pct:
            totalActual > 0 ? Number(r.actual || 0) / totalActual : 0,
        })),
        columns: [
          { key: "category_name", header: "Categoria" },
          { key: "occurrences", header: "Scadenze", type: "integer" },
          { key: "planned", header: "Pianificato (€)", type: "currency" },
          { key: "actual", header: "Pagato (€)", type: "currency" },
          { key: "variance", header: "Δ (€)", type: "currency" },
          { key: "share_pct", header: "Quota %", type: "percent" },
        ],
      });

      // Previsione mensile (clipped to selected period)
      const fcMonthlyClipped = (fcMonthly || []).filter((r) => {
        const m = String(r.month).padStart(2, "0");
        const iso = `${r.year}-${m}-01`;
        return inRange(iso, dateFrom.slice(0, 7) + "-01", dateTo);
      });
      sheets.push({
        name: "Previsione mensile",
        rows: fcMonthlyClipped.map((r) => ({
          period: `${r.year}-${String(r.month).padStart(2, "0")}`,
          forecast: r.forecast_amount,
          adjustment: r.adjustment_amount,
          total: r.total,
        })),
        columns: [
          { key: "period", header: "Mese" },
          { key: "forecast", header: "Previsione (€)", type: "currency" },
          { key: "adjustment", header: "Aggiustamenti (€)", type: "currency" },
          { key: "total", header: "Totale (€)", type: "currency" },
        ],
      });

      const fcYearTotal = (fcMonthly || []).reduce(
        (s, r) => s + Number(r.total || 0),
        0,
      );
      sheets.push({
        name: "Previsione categorie",
        rows: (fcCategory || []).map((c) => ({
          category_name: c.category_name,
          forecast: c.forecast_amount,
          share_pct:
            fcYearTotal > 0 ? Number(c.forecast_amount || 0) / fcYearTotal : 0,
        })),
        columns: [
          { key: "category_name", header: "Categoria" },
          { key: "forecast", header: "Importo (€)", type: "currency" },
          { key: "share_pct", header: "Quota %", type: "percent" },
        ],
      });

      const filenameBase = buildFilename(
        `report_periodo_${basis}` +
          (vendorId ? `_${(venMap[vendorId]?.name || "").replace(/\s+/g, "_")}` : "") +
          (categoryId ? `_${(catMap[categoryId]?.name || "").replace(/\s+/g, "_")}` : ""),
        { date_from: dateFrom, date_to: dateTo },
      );

      // CRITICAL: trigger the download while the dialog is STILL mounted.
      // The dialog stays open and switches to a "done" phase the user
      // explicitly dismisses. This eliminates the portal-lifecycle race that
      // previously caused "insertBefore" runtime errors:
      //   - no toast portal mounts after our temp <a> manipulates the DOM
      //   - no Radix Dialog unmounts in the same tick as the download
      //   - the user controls when the portal closes (single, isolated event)
      try {
        exportSheets({ sheets, filenameBase, format: "xlsx" });
      } catch (downloadErr) {
        // eslint-disable-next-line no-console
        console.error("Errore generazione file:", downloadErr);
        if (aliveRef.current) {
          setPhase("idle");
        }
        throw new Error(
          "Impossibile generare il file. Riprova oppure controlla i permessi del browser.",
        );
      }
      if (aliveRef.current) {
        setDownloadedName(`${filenameBase}.xlsx`);
        setPhase("done");
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      if (aliveRef.current) {
        setErrorMessage(
          e?.response?.data?.detail ||
            e?.message ||
            "Errore durante la generazione del report",
        );
      }
    } finally {
      if (aliveRef.current) setBusy(false);
    }
  };

  return (
    <>
      <Button
        variant="default"
        data-testid={`${testId}-trigger`}
        onClick={() => handleOpenChange(true)}
      >
        <FileSpreadsheet size={14} strokeWidth={1.8} className="mr-1.5" />
        Esporta tutto il periodo
      </Button>

      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent
          data-testid={`${testId}-dialog`}
          className="max-w-md"
        >
          <DialogHeader>
            <DialogTitle>
              {phase === "done"
                ? "Report generato"
                : "Esporta tutto il periodo"}
            </DialogTitle>
            <DialogDescription>
              {phase === "done"
                ? "Il file è stato scaricato dal browser. Se il download non è partito, controlla le autorizzazioni dei download del tuo browser."
                : "Genera un unico file Excel multi-foglio per l'archiviazione mensile — include scadenze, pagamenti, scostamento, analisi fornitori e categorie, e previsione. Tutti i fogli rispettano i filtri qui sotto."}
            </DialogDescription>
          </DialogHeader>

          {phase === "done" ? (
            <div
              data-testid={`${testId}-done-panel`}
              className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-4"
            >
              <div className="flex items-start gap-3">
                <CheckCircle2
                  size={20}
                  strokeWidth={2}
                  className="mt-0.5 shrink-0 text-emerald-700"
                />
                <div className="min-w-0">
                  <div className="text-sm font-medium text-emerald-900">
                    Download completato
                  </div>
                  <div className="mt-1 truncate font-mono text-[11px] text-emerald-700">
                    {downloadedName}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-stone-500">
                  Dal
                </Label>
                <Input
                  type="date"
                  data-testid={`${testId}-date-from`}
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-stone-500">
                  Al
                </Label>
                <Input
                  type="date"
                  data-testid={`${testId}-date-to`}
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                />
              </div>
            </div>

            <div>
              <Label className="text-[11px] uppercase tracking-wider text-stone-500">
                Base di analisi
              </Label>
              <ToggleGroup
                type="single"
                value={basis}
                onValueChange={(v) => v && setBasis(v)}
                className="mt-1 justify-start"
              >
                <ToggleGroupItem
                  value="cash"
                  data-testid={`${testId}-basis-cash`}
                >
                  Cassa
                </ToggleGroupItem>
                <ToggleGroupItem
                  value="accrual"
                  data-testid={`${testId}-basis-accrual`}
                >
                  Competenza
                </ToggleGroupItem>
              </ToggleGroup>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-stone-500">
                  Fornitore
                </Label>
                <Select
                  value={vendorId || "all"}
                  onValueChange={(v) => setVendorId(v === "all" ? "" : v)}
                >
                  <SelectTrigger data-testid={`${testId}-vendor`}>
                    <SelectValue placeholder="Tutti" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutti</SelectItem>
                    {vendors.map((v) => (
                      <SelectItem key={v.id} value={v.id}>
                        {v.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-stone-500">
                  Categoria
                </Label>
                <Select
                  value={categoryId || "all"}
                  onValueChange={(v) => setCategoryId(v === "all" ? "" : v)}
                >
                  <SelectTrigger data-testid={`${testId}-category`}>
                    <SelectValue placeholder="Tutte" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutte</SelectItem>
                    {categories.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {errorMessage && (
              <div
                data-testid={`${testId}-error`}
                className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800"
              >
                {errorMessage}
              </div>
            )}
            </div>
          )}

          <DialogFooter>
            {phase === "done" ? (
              <Button
                onClick={() => handleOpenChange(false)}
                data-testid={`${testId}-close-done`}
              >
                Chiudi
              </Button>
            ) : (
              <>
                <Button
                  variant="outline"
                  onClick={() => handleOpenChange(false)}
                  disabled={busy}
                >
                  Annulla
                </Button>
                <Button
                  data-testid={`${testId}-submit`}
                  onClick={generate}
                  disabled={busy}
                >
                  {busy ? (
                    <>
                      <Loader2 className="mr-1.5 animate-spin" size={14} />
                      Generazione…
                    </>
                  ) : (
                    <>
                      <Download size={14} strokeWidth={1.8} className="mr-1.5" />
                      Genera report Excel
                    </>
                  )}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
